import { useTranslation } from "@redotech/redo-i18n";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left_filled.svg";
import { Component, memo } from "react";
import { Location, useNavigate, useRouteError } from "react-router-dom";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import { Flex } from "./flex";
import { Header, Text } from "./text";

export const ErrorMessageSection = memo(function ErrorMessageSection({
  details: detailsProp,
  error: errorProp,
  includeBackButton,
  showNewIntercomMessage,
}: {
  details?: string;
  error?: Error | unknown;
  includeBackButton?: boolean;
  showNewIntercomMessage?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const routeError = useRouteError();
  const error = errorProp ?? routeError;
  console.error("Client side error page shown to user", { error });
  const details =
    detailsProp ??
    t(
      "If you need assistance or want to share your feedback, feel free to chat with us.",
    );
  const errorMessage =
    error instanceof Error
      ? error.message
      : error != null
        ? String(error)
        : undefined;
  return (
    <Flex align="center" dir="column" flex={3}>
      <Header>{t("Oops, we encountered an error")}</Header>
      <Text>{details}</Text>
      {errorMessage && (
        <Text textAlign="center" textColor="error" whiteSpace="pre-wrap">
          {errorMessage}
        </Text>
      )}
      <Flex gap="lg" mt="xl">
        {includeBackButton && (
          <RedoButton
            hierarchy="secondary"
            IconLeading={ArrowLeftIcon}
            onClick={() => navigate(-1)}
            size="xl"
            text={t("Go back")}
          />
        )}
        {showNewIntercomMessage && (
          <RedoButton
            hierarchy="primary"
            onClick={() => showNewIntercomMessage()}
            size="xl"
            text={t("Chat with us")}
          />
        )}
      </Flex>
    </Flex>
  );
});

export const ErrorPage = memo(function ErrorPage({
  error,
  showNewIntercomMessage,
}: {
  error?: Error;
  showNewIntercomMessage?: () => void;
}) {
  return (
    <Flex dir="column" flex={1}>
      <Flex flex={1} />
      <ErrorMessageSection
        error={error}
        includeBackButton
        showNewIntercomMessage={showNewIntercomMessage}
      />
    </Flex>
  );
});

// Must be a class component to act as an error boundary
// https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
export class ErrorBoundary extends Component<
  {
    children: React.ReactNode;
    location: Location;
    showNewIntercomMessage?: () => void;
  },
  { error: Error | null; hasError: boolean }
> {
  constructor(props: {
    children: React.ReactNode;
    location: Location;
    showNewIntercomMessage?: () => void;
  }) {
    super(props);
    this.state = { error: null, hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { error, hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
  }

  override componentDidUpdate(prevProps: { location: Location }) {
    if (this.state.hasError && this.props.location !== prevProps.location) {
      this.setState({ error: null, hasError: false });
    }
  }

  override render() {
    if (this.state.hasError) {
      return (
        <ErrorPage
          error={this.state.error ?? undefined}
          showNewIntercomMessage={this.props.showNewIntercomMessage}
        />
      );
    }

    return this.props.children;
  }
}
